// =====================================================================
// MesCourses.jsx - Vue chauffeur mobile-first (M-002 Faza 2 - Partea C)
// =====================================================================
// Replica modulul Odoo "Mes courses" pour Pierre + Austin (driver self-service).
//
// Permission gates:
//   - trips.read.assigned     -> vede DOAR propriile trips (backend filtreaza)
//   - trips.complete.assigned -> buton "Terminer" vizibil pe card-uri PLANNED
//
// UX: mobile-first (cards verticale, buton big-tap). Functioneaza si pe desktop
// dar layout-ul ramane card-based (no responsive switch).
//
// Flow:
//   1. fetchList() -> GET /trips (backend filtreaza la driverId === user.id)
//   2. Filter local: doar status PLANNED implicit (toggle pentru istoric)
//   3. Click "Terminer" -> prompt durée + comment -> POST /trips/:id/complete
//   4. onComplete success -> refetch list -> card-ul dispare din "À faire"
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { trips as tripsApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import CompletionModal from './CompletionModal.jsx';

const STATUS_LABELS_FR = {
  PLANNED:   'Planifié',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const STATUS_BADGE_STYLE = {
  PLANNED:   { background: '#fef3c7', color: '#92400e' },
  COMPLETED: { background: '#d1fae5', color: '#065f46' },
  CANCELLED: { background: '#fee2e2', color: '#991b1b' },
};

const PAGE_SIZE = 50;

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short',
    });
  } catch {
    return '';
  }
}

export default function MesCourses() {
  const { user, can } = useAuth();
  const canComplete = can('trips.complete.assigned');

  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [completingId, setComplId] = useState(null);
  // Day 11 Variant B — modal pour saisie km + duree per ligne (au lieu de window.prompt chain)
  const [tripBeingCompleted, setTripBeingCompleted] = useState(null);

  // Filter: 'todo' (PLANNED) | 'done' (COMPLETED) | 'all'
  const [view, setView] = useState('todo');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: 1, pageSize: PAGE_SIZE };
      if (view === 'todo') params.status = 'PLANNED';
      if (view === 'done') params.status = 'COMPLETED';
      // Backend filtreaza automat la driverId === user.id pentru trips.read.assigned only

      const res = await tripsApi.list(params);
      setItems(res.items || []);
    } catch (err) {
      console.error('[MesCourses] fetch error', err);
      if (err.response?.status === 403) {
        setError("Vous n'avez pas la permission d'accéder à cette page.");
      } else {
        setError(err.response?.data?.message || 'Erreur lors du chargement');
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Day 11 Variant B — ouvre modal mobile-first au lieu de window.prompt chain.
  function handleTerminer(trip) {
    if (!canComplete) return;
    if (trip.status !== 'PLANNED') return;
    const tripLines = Array.isArray(trip.lines) && trip.lines.length > 0 ? trip.lines : null;
    if (!tripLines) {
      alert('Trajet sans lignes projet. Contactez un superviseur.');
      return;
    }
    setTripBeingCompleted(trip);
  }

  async function handleSubmitCompletion(payload) {
    if (!tripBeingCompleted) return;
    setComplId(tripBeingCompleted.id);
    try {
      await tripsApi.complete(tripBeingCompleted.id, payload);
      setTripBeingCompleted(null);
      await fetchList();
    } catch (err) {
      console.error('[MesCourses] complete error', err);
      alert(err.response?.data?.message || 'Erreur lors de la complétion du trajet.');
    } finally {
      setComplId(null);
    }
  }

  const userName = user?.name || 'Chauffeur';

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Mes courses</h1>
        <p style={subtitleStyle}>Bonjour {userName}. Voici vos trajets assignés.</p>
      </div>

      {/* View toggle */}
      <div style={toggleStyle}>
        <button
          onClick={() => setView('todo')}
          style={toggleBtn(view === 'todo')}
        >
          À faire
        </button>
        <button
          onClick={() => setView('done')}
          style={toggleBtn(view === 'done')}
        >
          Terminés
        </button>
        <button
          onClick={() => setView('all')}
          style={toggleBtn(view === 'all')}
        >
          Tous
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={errorStyle}>{error}</div>
      )}

      {/* Loading */}
      {loading && (
        <div style={emptyStyle}>Chargement...</div>
      )}

      {/* Empty state */}
      {!loading && !error && items.length === 0 && (
        <div style={emptyStyle}>
          {view === 'todo' && 'Aucun trajet à faire. Bonne journée !'}
          {view === 'done' && 'Aucun trajet terminé pour le moment.'}
          {view === 'all' && 'Aucun trajet assigné.'}
        </div>
      )}

      {/* Cards */}
      {!loading && items.map(trip => (
        <div key={trip.id} style={cardStyle}>
          {/* Header card: ref + status */}
          <div style={cardHeaderStyle}>
            <span style={refBadge}>{trip.ref}</span>
            <span style={{
              ...statusPillBase,
              ...(STATUS_BADGE_STYLE[trip.status] || {}),
            }}>
              {STATUS_LABELS_FR[trip.status] || trip.status}
            </span>
          </div>

          {/* Project */}
          <div style={projectStyle}>
            {trip.project?.name || '—'}
          </div>

          {/* Itinerary */}
          <div style={routeStyle}>
            <strong>{trip.origin || '—'}</strong>
            <span style={{ color: '#9ca3af', margin: '0 6px' }}>→</span>
            <strong>{trip.destination || '—'}</strong>
          </div>

          {/* Meta line: date · km · attente */}
          <div style={metaStyle}>
            {formatDateFR(trip.date)}
            {trip.kmTotal != null && <> · {trip.kmTotal} km</>}
            {trip.waitingMinutes != null && trip.waitingMinutes > 0 && <> · attente {trip.waitingMinutes} min</>}
          </div>

          {/* Optional task template */}
          {trip.taskTemplate?.label && (
            <div style={taskStyle}>
              Tâche : {trip.taskTemplate.label}
            </div>
          )}

          {/* Optional commentaire from encoder */}
          {trip.commentaire && (
            <div style={commentStyle}>
              <strong>Note :</strong> {trip.commentaire}
            </div>
          )}

          {/* Completion info if done */}
          {trip.status === 'COMPLETED' && (
            <div style={completedInfoStyle}>
              Terminé
              {trip.completedAt && <> le {new Date(trip.completedAt).toLocaleString('fr-BE')}</>}
              {trip.durationMinutes != null && <> · durée {trip.durationMinutes} min</>}
              {trip.driverComment && (
                <div style={{ marginTop: 4, fontStyle: 'italic' }}>"{trip.driverComment}"</div>
              )}
            </div>
          )}

          {/* Action button */}
          {trip.status === 'PLANNED' && canComplete && (
            <button
              onClick={() => handleTerminer(trip)}
              disabled={completingId === trip.id}
              style={completingId === trip.id ? terminerBtnDisabled : terminerBtnStyle}
            >
              {completingId === trip.id ? '…' : '✓ Terminer'}
            </button>
          )}
        </div>
      ))}

      {/* Day 11 Variant B — modal de complétion mobile-first */}
      {tripBeingCompleted && (
        <CompletionModal
          trip={tripBeingCompleted}
          submitting={completingId === tripBeingCompleted.id}
          onClose={() => { if (completingId !== tripBeingCompleted.id) setTripBeingCompleted(null); }}
          onSubmit={handleSubmitCompletion}
        />
      )}
    </div>
  );
}

// =====================================================================
// Styles - mobile-first (max-width pe container, paddings comfortables)
// =====================================================================

const containerStyle = {
  maxWidth: 520,
  margin: '0 auto',
  padding: '1rem',
};

const headerStyle = {
  marginBottom: '1rem',
};

const titleStyle = {
  margin: 0,
  fontSize: '1.5rem',
  fontWeight: 600,
  color: '#1f2937',
};

const subtitleStyle = {
  margin: '0.25rem 0 0',
  color: '#6b7280',
  fontSize: '0.875rem',
};

const toggleStyle = {
  display: 'flex',
  gap: 4,
  background: '#f3f4f6',
  padding: 4,
  borderRadius: 6,
  marginBottom: '1rem',
};

const toggleBtn = (active) => ({
  flex: 1,
  padding: '0.5rem 0.75rem',
  background: active ? '#fff' : 'transparent',
  color: active ? '#1f2937' : '#6b7280',
  border: 'none',
  borderRadius: 4,
  fontSize: '0.875rem',
  fontWeight: active ? 500 : 400,
  cursor: 'pointer',
  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
});

const errorStyle = {
  padding: '0.75rem',
  background: '#fee2e2',
  color: '#991b1b',
  borderRadius: 4,
  marginBottom: '1rem',
  fontSize: '0.875rem',
};

const emptyStyle = {
  padding: '2rem',
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '0.875rem',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
};

const cardStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '0.875rem',
  marginBottom: '0.625rem',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 6,
};

const refBadge = {
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontSize: 11,
  color: '#0c447c',
  background: '#e6f1fb',
  padding: '2px 6px',
  borderRadius: 3,
};

const statusPillBase = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 3,
  fontSize: 11,
  fontWeight: 500,
};

const projectStyle = {
  fontSize: 14,
  fontWeight: 500,
  color: '#1f2937',
  marginBottom: 2,
};

const routeStyle = {
  fontSize: 13,
  color: '#374151',
  marginBottom: 6,
};

const metaStyle = {
  fontSize: 12,
  color: '#6b7280',
  marginBottom: 4,
};

const taskStyle = {
  fontSize: 12,
  color: '#6b7280',
  marginTop: 4,
};

const commentStyle = {
  fontSize: 12,
  color: '#374151',
  background: '#f9fafb',
  padding: '6px 8px',
  borderRadius: 4,
  marginTop: 6,
  border: '1px solid #e5e7eb',
};

const completedInfoStyle = {
  fontSize: 11,
  color: '#6b7280',
  marginTop: 6,
  padding: '6px 8px',
  background: '#f9fafb',
  borderRadius: 4,
  border: '1px solid #e5e7eb',
};

const terminerBtnStyle = {
  display: 'block',
  width: '100%',
  marginTop: 10,
  padding: '0.75rem',
  background: '#059669',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 500,
  cursor: 'pointer',
};

const terminerBtnDisabled = {
  ...{
    display: 'block',
    width: '100%',
    marginTop: 10,
    padding: '0.75rem',
    background: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 500,
  },
  opacity: 0.6,
  cursor: 'not-allowed',
};
